import React, { FC } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { usePurchaseInvoice } from '@hub/api/queries/use-purchase-queries';
import useExportDocuments from '@hub/hooks/use-export-documents';
import { SystemIcon } from '@monorepo/nova-icons';
import { Button, CircularProgress, Stack, Typography } from '@monorepo/nova-ui';
import { useDashboard } from '../../../contexts';
import { BannerTextContainer } from '../styled';
import { HiddenInvoice } from './hidden-invoice';
import { getButtonStyles, getResponsiveTypographyStyles } from './styles';

export const PurchaseSection: FC = () => {
  const { account } = useDashboard();
  const { t } = useTranslation('thankYou');

  const {
    data: invoice,
    isLoading,
    error,
    refetch,
  } = usePurchaseInvoice(account?.purchaseId || '');
  const { componentRef, handleDownloadPdf, inProgress } = useExportDocuments();

  const isEmpty = !invoice && !isLoading && !error;
  const isDownloading = inProgress;

  const getButtonIcon = () => {
    if (isDownloading || isLoading) {
      return <CircularProgress size={16} color="inherit" />;
    }

    if (error) {
      return <SystemIcon iconName="Warning" />;
    }

    if (isEmpty) {
      return <SystemIcon iconName="File" />;
    }

    return null;
  };

  const getButtonText = () => {
    if (isDownloading) {
      return t('downloading', 'Downloading...');
    }

    if (isLoading) {
      return t('loading', 'Loading...');
    }

    if (error) {
      return t('retry', 'Retry');
    }

    if (isEmpty) {
      return t('not_available', 'Not Available');
    }

    return t('download_invoice');
  };

  const handleButtonClick = async () => {
    if (error) {
      refetch();
      return;
    }

    if (invoice) {
      try {
        await handleDownloadPdf(invoice.purchase.purchaseId);
      } catch (downloadError) {
        console.error('Download failed:', downloadError);
        // Error handling could be enhanced with toast notification
      }
    }
  };

  const isButtonDisabled = isDownloading || isLoading || (!error && isEmpty);

  return (
    <BannerTextContainer>
      <Stack gap={4}>
        <Typography
          variant="h4"
          sx={(theme) => getResponsiveTypographyStyles(theme).title}
        >
          {t('thank_you_for_your_purchase')}
        </Typography>

        {account?.purchaseId && (
          <Typography
            variant="body2"
            sx={(theme) => getResponsiveTypographyStyles(theme).orderText}
          >
            {t('order_number', { orderNumber: account.purchaseId })}
          </Typography>
        )}

        <Button
          variant="primary"
          size="lg"
          disabled={isButtonDisabled}
          onClick={handleButtonClick}
          endIcon={<SystemIcon iconName="DownloadSimple" />}
          startIcon={getButtonIcon()}
          sx={(theme) => getButtonStyles(theme).downloadButton}
        >
          {getButtonText()}
        </Button>

        {invoice &&
          createPortal(
            <HiddenInvoice ref={componentRef} invoice={invoice} />,
            document.body,
          )}
      </Stack>
    </BannerTextContainer>
  );
};
